import React, { useState, useEffect } from 'react'

import FileUpload from '../form/FileUpload'
import Button from '../form/Button'
import ErrorMessage from '../errors/ErrorMessage'
import { useI18N } from '../../../libraries/i18n/hook'
import Loading from '../layout/Loading'

interface CVUploaderProps {
  onUpload: (file: File) => Promise<void>
  isUploading?: boolean
  uploadError?: Error | null
}

const CVUploader: React.FC<CVUploaderProps> = ({ onUpload, isUploading = false, uploadError = null }) => {
  const { t } = useI18N()
  const [error, setError] = useState<Error | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)

  // Use external loading state if provided, otherwise use internal state
  const isCurrentlyLoading = isUploading || loading

  const handleFileSelected = (selectedFile: File) => {
    setFile(selectedFile)
    setError(null)
  }

  const handleUpload = async () => {
    if (!file) {
      setError(new Error(t('cv.error.selectFile')))
      return
    }

    setLoading(true)
    setError(null)

    try {
      await onUpload(file)
    } catch (err) {
      setError(err instanceof Error ? err : new Error(t('cv.error.analysisFailed')))
    } finally {
      setLoading(false)
    }
  }

  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0)

  const loadingMessages = [
    t('cv.upload.loadingMessages.processing'),
    t('cv.upload.loadingMessages.takingTime'),
    t('cv.upload.loadingMessages.checkingFormat'),
    t('cv.upload.loadingMessages.analyzingStructure'),
    t('cv.upload.loadingMessages.checkingConsistency'),
    t('cv.upload.loadingMessages.stillWorking'),
    t('cv.upload.loadingMessages.almostThere'),
    t('cv.upload.loadingMessages.finalTouches')
  ]

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null

    if (isCurrentlyLoading) {
      interval = setInterval(() => {
        setLoadingMessageIndex((prevIndex) => (prevIndex + 1) % loadingMessages.length)
      }, 4000)
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [isCurrentlyLoading, loadingMessages.length])

  // Determine which error to show - external upload error takes precedence
  const displayError = uploadError || error

  return (
    <div className='bg-soft-green rounded-lg py-6 lg:p-12 max-w-4xl mx-auto'>
      <div className='flex flex-col items-center justify-center mb-4 md:mb-8 pb-4 md:pb-6 border-b border-gray-100'>
        <h2 className='leading-none font-semibold mb-1 md:mb-2'>{t('cv.upload.title')}</h2>
        <p className='mt-8 text-xs sm:text-md font-light mx-auto text-center'>
          {t('cv.upload.description')}
        </p>
      </div>

      <div className='grid grid-cols-3 lg:grid-cols-4 gap-3 w-full'>
        <div className='col-span-3'>
          <FileUpload
            onFileSelected={handleFileSelected}
            accept='.pdf'
            label=''
            disabled={isCurrentlyLoading}
            maxMbSize={5}
            defaultButtonLabel={{
              select: t('cv.upload.select'),
              change: t('cv.upload.change')
            }}
            hideBorder
          />
        </div>
        <div className='mt-5 lg:mt-0 col-span-3 lg:col-span-1 flex items-center justify-center'>
          <Button
            onClick={handleUpload}
            disabled={!file || isCurrentlyLoading}
            size='md'
            variant='primary'
            className='lg:w-full'
          >
            {isCurrentlyLoading ? t('cv.upload.analyzing') : t('cv.upload.analyze')}
          </Button>
        </div>
      </div>

      {isCurrentlyLoading && (
        <div className='mt-5 text-center min-h-[24px] transition-all duration-300 ease-in-out'>
          <Loading
            size='inline'
            message={loadingMessages[loadingMessageIndex]}
            textClassName='text-sm text-deep-green font-medium'
          />
        </div>
      )}

      {displayError && (
        <div className='mt-4 md:mt-6'>
          <ErrorMessage error={displayError} />
        </div>
      )}
    </div>
  )
}

export default CVUploader
